import React, { useCallback, useState } from 'react'
import { actionAtom } from 'atoms/atoms'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { ApiKeyCreateResponse, ApiKeyRequestForm } from 'types/apiKeys'
import { ApiKey2FAConfirmation } from './ApiKey2FAConfirmation'
import { ApiKeyCreateForm } from './ApiKeyCreateForm'
import ApiKeySuccess from './ApiKeySuccess'

type ApiKeyState =
  | { type: 'INIT' }
  | { type: 'CONFIRMATION'; payload: ApiKeyRequestForm }
  | { type: 'SUCCESS'; response: ApiKeyCreateResponse }

export const ApiKeyCreate = ({ from2FA }: { from2FA: boolean }) => {
  const { t } = useTranslation()

  const [state, setState] = useState<ApiKeyState>({ type: 'INIT' })

  const [apiKeyAction, setApiKeyAction] = useAtom(actionAtom)

  const goBackToInit = useCallback(() => {
    setState({ type: 'INIT' })
    setApiKeyAction(prev => ({
      ...prev,
      header: t('manageApiKeys.generateNewAPIKey'),
      onBack: undefined,
    }))
  }, [setApiKeyAction, t])

  const onSuccessCreateApiKey = useCallback(
    (payload: ApiKeyRequestForm) => {
      setState({ type: 'CONFIRMATION', payload })
      setApiKeyAction({
        ...apiKeyAction,
        header: t('manageApiKeys.confirmNewAPIKey'),
        onBack: goBackToInit,
      })
    },
    [apiKeyAction, goBackToInit, t]
  )

  const onSuccess2FAApiKey = useCallback(
    (response: ApiKeyCreateResponse) => {
      setState({ type: 'SUCCESS', response })
      setApiKeyAction({
        ...apiKeyAction,
        header: '',
        onBack: undefined,
      })
    },
    [apiKeyAction]
  )

  if (state.type === 'CONFIRMATION') {
    return (
      <ApiKey2FAConfirmation setShowSuccessModal={onSuccess2FAApiKey} payload={state.payload} />
    )
  }

  if (state.type === 'SUCCESS') {
    return (
      <ApiKeySuccess response={state.response} setShowSuccess={() => setState({ type: 'INIT' })} />
    )
  }

  return <ApiKeyCreateForm onSuccessCreateApiKey={onSuccessCreateApiKey} from2FA={from2FA} />
}
